import React from 'react';
import { PhoneIcon, UsersIcon, UserIcon, ListBulletIcon, WrenchScrewdriverIcon,
    PrinterIcon, LifebuoyIcon, RectangleGroupIcon, SignalIcon, GlobeAltIcon, Squares2X2Icon,
    ComputerDesktopIcon, TruckIcon, ServerStackIcon, CloudIcon, RocketLaunchIcon, QuestionMarkCircleIcon
 } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import { NavigationHeader } from './NavigationHeader';
import { Footer } from './Footer';

const phoneNumber = '(817) 524-8117';

const localSupport = [
    { name: 'In-Person Support', description: 'In-Person IT support from a local expert serving Overland Park and surrounding communities.', icon: UserIcon },
    { name: 'Remote Support', description: 'Remote support is available for businesses that need immediate assistance.', icon: PhoneIcon },
    { name: 'Project Support', description: 'Local on-site technicians to help support IT projects such as PC deployments, workstation moves, or migrations.', icon: ListBulletIcon },
    { name: 'Staff Augmentation', description: 'In-Person IT technicians assigned to your business for in-house support, part-time or full-time.', icon: UsersIcon },
]

const hero = {
    title: 'Overland Park IT',
    description:
      'Providing IT Services and Support for businesses in Overland Park, KS and surrounding communities. Overland Park IT is a locally owned family business.',
}

const local = {
    title: 'Local IT Support When You Need It Most',
    description:
      'Get a local IT expert to help with your IT issues either in-person or remotely.',
}

const pricing = {
    title: 'Pricing Examples for Common Scenarios',
    description:
      'Below are some common support pricing examples for businesses in Overland Park and surrounding communities. Contact us for a custom quote.',
}

const services = {
    title: 'IT Services',
    description:
      'We offer a wide range of services to help you keep your business growing and running smoothly. Don\'t see what you need? Contact us.',
}

const cta = {
    title: 'Ready to get started?',
    description:
      'Contact us today to get started with your IT support services. We are here to help you with all your IT needs.',
}

const features = [
    {
      name: 'Break-Fix Support',
      description:
        'Break-Fix for desktops, laptops, servers, and network equipment.',
      icon: WrenchScrewdriverIcon,
    },
    {
        name: 'Imaging & Deployment',
        description:
            'Imaging and deployment services for computers and devices.',
        icon: ComputerDesktopIcon,
    },
    {
        name: 'Application Support',
        description:
            'Application setup, configuration, and support services.',
        icon: RectangleGroupIcon
    },
    {
        name: 'Network Support',
        description:
            'Routers, switches, and WiFi installation, configuration, and support services.',
        icon: SignalIcon
    },
    {
        name: 'Cabling & Wiring',
        description:
            'Cabling and wiring services for network and VOIP phone systems.',
        icon: TruckIcon
    },
    {
        name: 'Data Backup & Recovery',
        description:
            'Data backup and recovery support services.',
        icon: LifebuoyIcon
    },
    {
        name: 'Server Support',
        description:
            'Server support services for Windows and Linux servers.',
        icon: ServerStackIcon
    },
    {
        name: 'Printer Support',
        description:
            'Local and network printer support services.',
        icon: PrinterIcon
    },
    {
        name: 'Microsoft 365 Support',
        description:
            'Microsoft 365, Office 365, and Exchange support services.',
        icon: Squares2X2Icon
    },
    {
        name: 'MS Intune Support',
        description:
            'MS Intune management services for endpoints and users.',
        icon: CloudIcon
    },
    {
        name: 'Website Support',
        description:
            'Website design, hosting, and support services.',
        icon: GlobeAltIcon
    },
    {
        name: 'E-Waste Removal & Recycling',
        description:
            'Removal and recycling of old IT equipment.',
        icon: TruckIcon
    }
]

const tiers = [
    {
      name: 'Managed IT Services',
      id: 'managed-it-services',
      href: '#',
      priceMonthly: '$50',
      priceLine: '/month per user',
      description: 'Offload your day-to-day IT management to a local expert.',
      features: [
        'User & Device Management',
        'Intune Management',
        'Office 365 Management',
        'Email Support',
        'Service Level Agreements'
      ],
      mostPopular: false,
    },
    {
      name: 'In-Person Service Call',
      id: 'in-person-service-call',
      href: '#',
      priceMonthly: '$60',
      priceLine: 'service call fee',
      description: 'Service call minimum includes remedy of common IT issues. Maximum of 30 minutes. Additional time will be billed after appoval by customer.',
      features: [
        'No Contracts', 
        'In Service Area', 
        'Up to 30 Minutes'
        ],
      mostPopular: true,
    },
    {
      name: 'Website Hosting',
      id: 'website-hosting',
      href: '#',
      priceMonthly: '$99',
      priceLine: '/month',
      description: 'Host your website on our servers with 99.9% uptime.',
      features: [
        'Custom Website Hosting',
        'Wordpress Hosting',
        'SSL Certificates',
        'Domain Registration',
        '24-hour support'
      ],
      mostPopular: false,
    },
  ]

  const faqs = [
    {
      id: 1,
      question: "Do you offer remote support?",
      answer:
        "Yes, we offer remote support for businesses that need immediate assistance. We use Quick Assist or TeamViewer to connect to your computer and resolve issues.",
    },
    {
        id: 2,
        question: "Do I need an account to get remote assistance?",
        answer: "Yes, you will need to establish an account for payment of services before remote assistance can be offered."
    },
    {
        id: 3,
        question: "Can I get support for my home office?",
        answer: "Yes, we offer support for home offices. We can help with setting up a new computer, printer, or network equipment."
    },
    {
        id: 4,
        question: "Do you offer service contracts?",
        answer: "Service contracts are not necessary, but are available for businesses that need them."
    },
    {
        id: 5,
        question: "Do you pickup old equipment for recycling?",
        answer: "Yes, we can collect your older equipment for recycling. We will ensure that all data is securely wiped before recycling."
    },
    {
        id: 6,
        question: "Do you offer website hosting and design?",
        answer: "Yes, we can host your website on our servers and we offer website development services."
    }
  ]

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

export const Home = () => {

    return (
        <React.Fragment>
            <div className='bg-white'>
                <NavigationHeader />
                

                <main className='isolate'>

                    {/* Hero Section */}
                    <div className='flex items-center min-h-screen mt-4 py-36 lg:mt-14 lg:py-64'>
                        {/* Start troubleshooting here */}
                        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
                            <div className='mx-auto max-w-2xl text-center'>
                                <h1 className='text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl'>
                                    {hero.title}
                                </h1>
                                <p className='mt-6 font-semibold text-lg leading-8 text-gray-600'>
                                    {hero.description}
                                </p>
                                <div className='mt-10 flex items-center justify-center gap-x-6'>
                                    <a href="tel:817-524-8117" className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                                        Call {phoneNumber}
                                    </a>
                                    <a href="#services-offered" className="text-sm font-semibold leading-6 text-gray-900">
                                        Services Available <span aria-hidden="true">→</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Local Section */}
                    <div id='local-support' className='flex items-center bg-gray-800 py-20 min-h-screen '>
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl lg:text-center">
                                {/* <h2 className="text-2xl font-semibold leading-7 text-indigo-600">Local Support</h2> */}
                                <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{local.title}</h2>
                                <p className="mt-6 text-lg leading-8 text-gray-300">{local.description}</p>
                            </div>
                            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
                                <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
                                {localSupport.map((feature) => (
                                    <div key={feature.name} className="relative pl-16">
                                    <dt className="text-base font-semibold leading-7 text-white">
                                        <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                                        <feature.icon aria-hidden="true" className="h-6 w-6 text-white" />
                                        </div>
                                        {feature.name}
                                    </dt>
                                    <dd className="mt-2 text-base leading-7 text-gray-300">{feature.description}</dd>
                                    </div>
                                ))}
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Services section */}
                    <div id='services-offered' className="flex items-center py-20 min-h-screen">
                        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
                            <div className="mx-auto max-w-2xl lg:text-center">
                                {/* <h2 className="text-base font-semibold leading-7 text-indigo-600">Everything you need</h2> */}
                                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{services.title}</h2>
                                <p className="mt-6 text-lg leading-8 text-gray-600">{services.description}</p>
                            </div>
                            <div className="mx-auto mt-16 px-6 sm:mt-20 md:mt-24 lg:px-8">
                                <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base leading-7 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
                                {features.map((feature) => (
                                    <div key={feature.name} className="relative pl-9">
                                    <dt className="inline font-semibold text-gray-900">
                                        <feature.icon aria-hidden="true" className="absolute left-1 top-1 h-5 w-5 text-indigo-600" />
                                        {feature.name}
                                    </dt>{' '}
                                    <dd className="inline">{feature.description}</dd>
                                    </div>
                                ))}
                                </dl>
                            </div>
                        </div>
                    </div>


                    <div id="pricing" className='min-h-screen py-20 bg-gray-800'>
                        {/* Pricing Section */}
                        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
                                <div className="mx-auto max-w-4xl text-center">
                                    {/* <h2 className="text-2xl font-semibold leading-7 text-indigo-600">Pricing</h2> */}
                                    <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">{pricing.title}</h2>
                                </div>
                                <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-300">{pricing.description}</p>
                                <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                {tiers.map((tier, tierIdx) => (
                                    <div
                                    key={tier.id}
                                    className={classNames(
                                        tier.mostPopular ? 'lg:z-10 lg:rounded-b-none' : 'lg:mt-8',
                                        tierIdx === 0 ? 'lg:rounded-r-none' : '',
                                        tierIdx === tiers.length - 1 ? 'lg:rounded-l-none' : '',
                                        'flex flex-col justify-between rounded-3xl bg-white p-8 ring-1 ring-gray-200 xl:p-10',
                                    )}
                                    >
                                    <div>
                                        <div className="flex items-center justify-between gap-x-4">
                                        <h3
                                            id={tier.id}
                                            className={classNames(
                                            tier.mostPopular ? 'text-indigo-600' : 'text-gray-900',
                                            'text-lg font-semibold leading-8',
                                            )}
                                        >
                                            {tier.name}
                                        </h3>
                                        {tier.mostPopular ? (
                                            <p className="rounded-full bg-indigo-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600">
                                            Most popular
                                            </p>
                                        ) : null}
                                        </div>
                                        {tier.mostPopular ? (
                                                <p className="mt-4 text-sm leading-6 text-gray-600">{tier.description}</p>
                                            ) : null
                                        }
                                        
                                        <p className="mt-6 flex items-baseline gap-x-1">
                                        <span className="text-4xl font-bold tracking-tight text-gray-900">{tier.priceMonthly}</span>
                                        <span className="text-sm font-semibold leading-6 text-gray-600">{tier.priceLine}</span>
                                        </p>
                                        <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                                        {tier.features.map((feature) => (
                                            <li key={feature} className="flex gap-x-3">
                                            <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-indigo-600" />
                                            {feature}
                                            </li>
                                        ))}
                                        </ul>
                                    </div>
                                    </div>
                                ))}
                                </div>
                            
                        </div>

                        {/* Contact Section */}
                        <div className="bg-gray-800 py-8">
                            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                                <h2 className="text-3xl font-bold leading-10 tracking-tight text-white sm:text-4xl">Call Us</h2>
                                <div className='mt-10'>

                                    <div className="flex items-center gap-x-6">
                                        <a
                                        href="tel:817-524-8117"
                                        className="rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                                        >
                                        Call {phoneNumber}
                                        </a>
                                    </div>

                                </div>
                                {/* <p className="mt-6 max-w-2xl text-base leading-7 text-gray-300">
                                Would you like us to contact you? Leave us your contact details and description of your business requirements by{' '}
                                <a href="/contact-form" className="font-semibold text-indigo-400 hover:text-indigo-300">
                                    submitting our contact form
                                </a>{' '}
                                and we will get back to you as soon as we can.
                                </p> */}

                            </div>
                        </div>
                    </div>


                    {/* FAQ Section */}
                    <div id="faq" className="flex items-center py-20 min-h-screen">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <h2 className="text-2xl font-bold leading-10 tracking-tight text-gray-900">Frequently asked questions</h2>
                            {/* <p className="mt-6 max-w-2xl text-base leading-7 text-gray-600">
                            Have a different question and can’t find the answer you’re looking for? Submit your question by{' '}
                            <a href="/contact-form" className="font-semibold text-indigo-400 hover:text-indigo-300">
                                submitting our contact form
                            </a>{' '}
                            and we’ll get back to you as soon as we can.
                            </p> */}
                            <div className="mt-10">
                                <dl className="space-y-16 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-16 sm:space-y-0 lg:grid-cols-3 lg:gap-x-10">
                                    {faqs.map((faq) => (
                                    <div key={faq.id}>
                                        <dt className="text-base font-semibold leading-7 text-gray-900">{faq.question}</dt>
                                        <dd className="mt-2 text-base leading-7 text-gray-600">{faq.answer}</dd>
                                    </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </div>
                    
                    {/* Contact and Service Area */}
                    <div id="contact" className='flex items-center py-20 bg-gray-800'>
                        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
                            <div>
                                <h2 className="text-3xl font-bold leading-10 tracking-tight text-white sm:text-4xl">Contact Sales & Support</h2>
                                {/* <p className="mt-6 max-w-2xl text-base leading-7 text-gray-300">
                                Would you like us to contact you? Leave us your contact details and description of your business requirements by{' '}
                                <a href="/contact-form" className="font-semibold text-indigo-400 hover:text-indigo-300">
                                    submitting our contact form
                                </a>{' '}
                                and we will get back to you as soon as we can.
                                </p> */}
                                <div className='mt-10'>

                                    <div className="flex items-center gap-x-6">
                                        <a
                                        href="tel:817-524-8117"
                                        className="rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
                                        >
                                        Call {phoneNumber}
                                        </a>
                                    </div>

                                </div>

                                {/* <div className="mt-6 flex items-center gap-x-6">
                                    <div className="ml-4 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                                        <LifebuoyIcon aria-hidden="true" className="h-6 w-6 text-white" />
                                    </div>
                                    <div className='flex h-10 items-center justify-center text-white text-lg'>
                                            <div>Option 1 Support</div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center gap-x-6">
                                    <div className="ml-4 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                                        <RocketLaunchIcon aria-hidden="true" className="h-6 w-6 text-white" />
                                    </div>
                                    <div className='flex h-10 items-center justify-center text-white text-lg'>
                                            <div>Option 2 Sales</div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center gap-x-6">
                                    <div className="ml-4 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                                        <QuestionMarkCircleIcon aria-hidden="true" className="h-6 w-6 text-white" />
                                    </div>
                                    <div className='flex h-10 items-center justify-center text-white text-lg'>
                                            <div>Option 4 Other Inquiries</div>
                                    </div>
                                </div> */}
                            </div>
                            <div id='service-area' className='mt-10'>
                                <h2 className="text-3xl font-bold leading-10 tracking-tight text-white sm:text-4xl">Service Area</h2>
                                <p className="mt-10 max-w-2xl text-base leading-7 text-gray-300">
                                We mainly service these areas, but at times may be flexible to your requirements for areas outside of those mentioned.
                                </p>
                                

                                <div className="mt-6 flex flex-row items-center gap-x-6">
                                    <div className="ml-4 flex h-10 font-semibold items-center justify-center text-white">
                                        Overland Park, KS
                                    </div>
                                    <div className="ml-4 flex h-10 font-semibold items-center justify-center text-white">
                                        Olathe, KS
                                    </div>
                                    <div className="ml-4 flex h-10 font-semibold items-center justify-center text-white">
                                        Leawood, KS
                                    </div>
                                    <div className="ml-4 flex h-10 font-semibold items-center justify-center text-white">
                                        Lenexa, KS
                                    </div>
                                    <div className="ml-4 flex h-10 font-semibold items-center justify-center text-white">
                                        Prairie Village, KS
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    
                    <Footer dark={true} />


                </main>
                

            </div>
        </React.Fragment>
    );
}